<template>
	<label
	:class="[{
		'error-state': errorState,
		'primary-color': primaryColor,
		'prevent-label-auto-capitalize': preventLabelAutoCapitalize,
		'with-tooltip': tooltipInfo,
        'multiple-lines': multipleLines
	}]">
		{{text}} <span v-if="mandatory">*</span>
		<!-- Tooltip And Element to trigger it -->
		<tooltips-generic
		v-if="tooltipInfo"
		:title="tooltipInfo.title"
		:description="tooltipInfo.description"
		:text="tooltipInfo.text"
		:size="tooltipInfo.size"
		:negative="tooltipInfo?.negative"
		:leftButtonText="tooltipInfo?.leftButtonText"
		:rightButtonText="tooltipInfo?.rightButtonText"
		:applyPositionFixed="tooltipInfo?.applyPositionFixed"
		@leftButtonClicked="emit('tooltipLeftButtonClicked')"
		@rightButtonClicked="emit('tooltipRightButtonClicked')">
			<template v-slot:slotElement="{ onClick }">
				<app-icon class="icon" @click.stop="onClick"/>
			</template>
		</tooltips-generic>
	</label>
</template>

<script setup>
import TooltipsGeneric from '@/components/generic/tooltips/Generic'

/* Vue */
import { computed, defineAsyncComponent } from 'vue'

/* Emmitters */
const emit = defineEmits(['tooltipLeftButtonClicked', 'tooltipRightButtonClicked'])

/** props received
@text { String }
@mandatory { Boolean }
@errorState { Boolean }
@primaryColor { Boolean }
@preventLabelAutoCapitalize {Boolean} prop to prevent label from capitalizing text
@multipleLines { Boolean } break label into multiple lines
@tooltipInfo {Object} with:
	- title {String} - tooltip title
	- text {String} - tooltip text
	- description {String} - tooltip description
	- negative {Boolean} - prop to apply negative styles
	- size {String} can be [medium, small, tiny (default)]
	- leftButtonText {String} - text for left button, has default text if this is not passed
	- rightButtonText {String} - text for right button, has default text if this is not passed
	- applyPositionFixed {Boolean} - prop to force tooltipo to use fixed position
@tooltipIcon {String} - icon name to trigger tooltip
*/
const props = defineProps({
	text: String,
	mandatory: Boolean,
	errorState: Boolean,
	primaryColor: Boolean,
    preventLabelAutoCapitalize: Boolean,
    multipleLines: Boolean,
	tooltipInfo: Object,
	tooltipIcon: String
})

/* Computed */
const appIcon = computed(() => {
    const icon = props.tooltipIcon || 'help'
	return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
})
</script>

<style lang="scss" scoped>
label {
	width: 100%;
	height: auto;
	min-height: 16px;

	/* This is needed for first-letter css prop to work, if this give us issues in the future remove this and use javascript to uppercase first letter */
	display: inline-block;

	@include H7-Regular;
	line-height: 16px;
	color: var(--secondary-dark-1);

	overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

	&:not(.prevent-label-auto-capitalize) {
		&:first-letter {
			text-transform: capitalize;
		}
	}

    &.multiple-lines {
        overflow: visible;
        white-space: normal;
    }

	&.with-tooltip {
		overflow: visible;
		white-space: unset;
	}

	.tooltip-container {
		width: 16px;
		height: 16px;
		margin-left: 8px;

		display: inline-block;

		.icon {
			cursor: pointer;
			transform: translateY(2px);

			:deep(.change-color-fill) {
				fill: var(--secondary-dark-2);
			}
		}
	}

	&.primary-color {
		&:not(.error-state) {
			color: var(--primary);
		}
	}

	&.error-state {
		color: var(--negative);
	}
}
</style>
